import React, { useState } from "react";
import {
  Box,
  Grid,
  Typography,
  styled,
  Drawer,
  IconButton,
  useMediaQuery,
  useTheme,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  AppBar,
  Toolbar,
} from "@mui/material";
import { Menu as MenuIcon, Close as CloseIcon } from "@mui/icons-material";
import Packages from "./packages";
import PromoCodes from "./promo-codes";
import Images from "./images";
import ContactNumber from "./contact-no";
import Franchise from "./franchise";
import CareerAppications from "./career-appications";
import CheckConsent from "./check-consent";
import Birthday from "./birthday";
import TokenManagement from "./token";
import CustomerDataTable from "./customerData";

const NavBtn = styled(Typography)(({ active }) => ({
  padding: "12px 20px",
  cursor: "pointer",
  borderBottom: active ? "3px solid #4f46e5" : "3px solid transparent",
  fontWeight: active ? "600" : "500",
  fontSize: "16px",
  color: active ? "#4f46e5" : "#64748b",
  transition: "all 0.3s ease",
  borderRadius: "8px 8px 0 0",
  background: active ? "rgba(79, 70, 229, 0.05)" : "transparent",
  whiteSpace: "nowrap",
  "&:hover": {
    color: "#4f46e5",
    background: "rgba(79, 70, 229, 0.08)",
    transform: "translateY(-2px)",
  },
}));

const SidebarItem = styled(ListItemButton)(({ theme, active }) => ({
  borderRadius: "12px",
  margin: "4px 0",
  backgroundColor: active ? "rgba(255, 255, 255, 0.2)" : "transparent",
  border: active
    ? "1px solid rgba(255, 255, 255, 0.3)"
    : "1px solid transparent",
  boxShadow: active ? "0 4px 15px rgba(0, 0, 0, 0.1)" : "none",
  "&:hover": {
    backgroundColor: active
      ? "rgba(255, 255, 255, 0.25)"
      : "rgba(255, 255, 255, 0.1)",
    transform: "translateX(4px)",
    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.15)",
  },
  transition: "all 0.3s ease",
  "& .MuiListItemText-primary": {
    fontWeight: active ? "700" : "400",
    color: active ? "white" : "rgba(255, 255, 255, 0.9)",
    fontSize: "15px",
  },
}));

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  "& .MuiDrawer-paper": {
    width: 280,
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    border: "none",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
  },
}));

const MobileNavContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  overflowX: "auto",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
  padding: "0 10px",
  gap: "8px",
}));

const AdminPortal = () => {
  const [active, setActive] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("xl"));

  const navItems = [
    "Packages", // index 0
    "Promo Codes", // index 1
    "Images", // index 2
    "CustomerData", // index 3
    "Contact Numbers", // index 4
    "Franchises", // index 5
    "Career Applications", // index 6
    "Birthday", // index 7
    "Token", // index 8
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavClick = (index) => {
    setActive(index);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const getActivePage = () => {
    switch (active) {
      case 0:
        return <Packages />;
      case 1:
        return <PromoCodes />;
      case 2:
        return <Images />;
      case 3:
        return <CustomerDataTable />;
      case 4:
        return <ContactNumber />;
      case 5:
        return <Franchise />;
      case 6:
        return <CareerAppications />;
      case 7:
        return <Birthday />;
      case 8:
        return <TokenManagement />;
      default:
        return <Packages />;
    }
  };

  const sidebarContent = (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: "700", color: "white" }}>
          Admin Portal
        </Typography>
        {isMobile && (
          <IconButton onClick={handleDrawerToggle} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.2)", mb: 2 }} />

      {/* Navigation Items */}
      <List sx={{ padding: 0 }}>
        {navItems.map((item, index) => (
          <ListItem key={index} disablePadding>
            <SidebarItem
              active={active === index}
              onClick={() => handleNavClick(index)}
            >
              <ListItemText
                primary={item}
                sx={{
                  "& .MuiListItemText-primary": {
                    color:
                      active === index ? "white" : "rgba(255, 255, 255, 0.9)",
                    fontWeight: active === index ? "700" : "400",
                    fontSize: "15px",
                  },
                }}
              />
            </SidebarItem>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Grid
      sx={{ minHeight: "100vh", backgroundColor: "#f8fafc", display: "flex" }}
    >
      {/* Mobile Header with Hamburger - Below main navbar */}
      {isMobile && (
        <Box
          sx={{
            position: "fixed",
            top: "80px",
            left: 0,
            right: 0,
            backgroundColor: "white",
            borderBottom: "1px solid #e5e7eb",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
            zIndex: 100,
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <IconButton
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, color: "#4f46e7" }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: "600", color: "#1f2937" }}>
            {navItems[active]}
          </Typography>
        </Box>
      )}

      {/* Sidebar Drawer */}
      <StyledDrawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block" },
          "& .MuiDrawer-paper": {
            position: isMobile ? "fixed" : "relative",
            height: "100vh",
            zIndex: isMobile ? 1300 : "auto",
            width: 280,
          },
        }}
      >
        {sidebarContent}
      </StyledDrawer>

      {/* Main Content Area */}
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          overflow: "hidden",
        }}
      >
        {/* Content Area */}
        <Grid
          sx={{
            margin: isMobile ? "0" : "0 20px 0 0",
            marginTop: isMobile ? "150px" : "0px",
            backgroundColor: "white",
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            height: isMobile ? "calc(100vh - 170px)" : "100vh",
            overflowY: "auto",
            overflowX: "hidden",
            flexGrow: 1,
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#888",
              borderRadius: "10px",
              "&:hover": {
                background: "#555",
              },
            },
          }}
        >
          {getActivePage()}
        </Grid>
      </Box>
    </Grid>
  );
};

export default AdminPortal;
